import { Fragment, isValidElement } from "react";
import type { ReactNode } from "react";
import { portableFilename } from "@strat/format";

import { currentDateDisplay } from "../utils/format";
import { XLSX_MEDIA_TYPE, xlsxBytes } from "./xlsx";
import type { XlsxCell } from "./xlsx";

/**
 * Tables out to a file: CSV for anything, .xlsx for Excel.
 *
 * A table hands over typed cells — numbers stay numbers and times stay times —
 * so a spreadsheet can sum a P&L column or sort by entry time without
 * re-parsing what the screen shows. Every row goes, not just the page in view,
 * in the order the table is sorted.
 */

export type ExportCell = XlsxCell;
export type TableFormat = "csv" | "xlsx";

export interface ExportColumn<T> {
  header: string;
  value: (row: T) => ExportCell;
}

export interface TableData {
  headers: string[];
  rows: ExportCell[][];
}

export function tableData<T>(columns: ExportColumn<T>[], rows: readonly T[]): TableData {
  return {
    headers: columns.map((c) => c.header),
    rows: rows.map((row) => columns.map((c) => c.value(row))),
  };
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** 2026-10-23 14:30:05, on the reader's clock (Settings → Display) — sortable as text too. */
function isoDateTime(date: Date): string {
  const utc = currentDateDisplay().timeZone === "utc";
  const y = utc ? date.getUTCFullYear() : date.getFullYear();
  const mo = (utc ? date.getUTCMonth() : date.getMonth()) + 1;
  const d = utc ? date.getUTCDate() : date.getDate();
  const h = utc ? date.getUTCHours() : date.getHours();
  const mi = utc ? date.getUTCMinutes() : date.getMinutes();
  const s = utc ? date.getUTCSeconds() : date.getSeconds();
  return `${y}-${pad(mo)}-${pad(d)} ${pad(h)}:${pad(mi)}:${pad(s)}`;
}

function csvField(value: ExportCell): string {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? isoDateTime(value) : "";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value === "boolean") return value ? "true" : "false";
  // A cell starting with = + - @ is a formula to a spreadsheet, and names and
  // descriptions here are written by other people. A leading apostrophe keeps
  // it text.
  let text = /^[=+\-@\t\r]/.test(value) && !/^[-+]?\d/.test(value) ? `'${value}` : value;
  if (/[",\r\n]/.test(text)) text = `"${text.replace(/"/g, '""')}"`;
  return text;
}

/** RFC 4180, with a byte-order mark so Excel reads it as UTF-8. */
export function toCsv({ headers, rows }: TableData): string {
  const lines = [headers.map(csvField).join(","), ...rows.map((r) => r.map(csvField).join(","))];
  return `\uFEFF${lines.join("\r\n")}\r\n`;
}

export function toXlsx(sheet: string, { headers, rows }: TableData): Uint8Array {
  return xlsxBytes(sheet, headers, rows, { utc: currentDateDisplay().timeZone === "utc" });
}

/** Hands a blob to the browser (or the desktop app's save dialog) as a download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked later: some browsers start the download asynchronously.
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/** "trades-btcusdt-4h-2026-10-23.csv" */
export function exportFilename(parts: (string | null | undefined)[], suffix: string): string {
  const d = new Date();
  return portableFilename(
    [...parts, `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`],
    suffix,
  );
}

/** Writes the table as a file and resolves with the name it was given. */
export function downloadTable(
  format: TableFormat,
  data: TableData,
  options: { name: (string | null | undefined)[]; sheet?: string },
): string {
  const filename = exportFilename(options.name, format === "csv" ? ".csv" : ".xlsx");
  const blob =
    format === "csv"
      ? new Blob([toCsv(data)], { type: "text/csv;charset=utf-8" })
      : new Blob([toXlsx(options.sheet ?? options.name.find(Boolean) ?? "Sheet1", data) as BlobPart], {
          type: XLSX_MEDIA_TYPE,
        });
  saveBlob(blob, filename);
  return filename;
}

// --- reading cells off a rendered table --------------------------------------------------------

/**
 * The text a cell shows, when it can be read without rendering: strings,
 * numbers and plain elements. Null when a component is in the way — its
 * output is only known by rendering it.
 */
export function nodeText(node: ReactNode): string | null {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) {
    let out = "";
    for (const child of node as ReactNode[]) {
      const text = nodeText(child);
      if (text === null) return null;
      out += text;
    }
    return out;
  }
  if (isValidElement(node)) {
    if (typeof node.type === "string" || node.type === Fragment) {
      return nodeText((node.props as { children?: ReactNode }).children);
    }
    return null;
  }
  return null;
}

/**
 * A number as the screen formats it — "+1.23%", "−4.5%", "1,234.56", "2×" —
 * back to the number, or null when the text is not just a number.
 */
export function displayNumber(text: string): number | null {
  const clean = text
    .trim()
    .replace(/−/g, "-")
    .replace(/^\+/, "")
    .replace(/[$€£,\s]/g, "")
    .replace(/[%×]$/, "");
  return /^-?\d+(\.\d+)?$/.test(clean) ? Number(clean) : null;
}

/** Epoch milliseconds between 1973 and 2286: a timestamp, not a quantity. */
export function looksLikeTimestamp(value: unknown): value is number {
  return typeof value === "number" && value > 1e11 && value < 1e13;
}

/**
 * The best cell for a column that only says how to render and how to sort:
 * the number the screen shows, else the time it sorts by, else the text.
 */
export function cellFromDisplay(rendered: ReactNode, sortValue: unknown): ExportCell {
  const text = nodeText(rendered);
  if (text !== null) {
    const n = displayNumber(text);
    if (n !== null) return n;
  }
  if (looksLikeTimestamp(sortValue)) return new Date(sortValue);
  if (text !== null && text.trim() !== "") {
    const t = text.trim();
    if (t === "—" || t === "–" || t === "-") return null;
    // "1.2k" and friends: the screen abbreviates, the sort value is exact.
    if (typeof sortValue === "number" && Number.isFinite(sortValue) && /\d/.test(t)) return sortValue;
    return t;
  }
  if (typeof sortValue === "number" || typeof sortValue === "string") return sortValue;
  return null;
}
